import React, { useState } from 'react';
import './ShippingDetailsModal.css';

function ShippingDetailsModal({ isOpen, onClose, creatorData, onProductShipped }) {
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [trackingDetails, setTrackingDetails] = useState('');
  const [deliveryCompany, setDeliveryCompany] = useState('');

  if (!isOpen || !creatorData) return null;

  const handleProductShipped = async () => {
    setIsLoading(true);
    try {
      await onProductShipped(creatorData, trackingDetails, deliveryCompany);
      setShowConfirmation(false);
      setTrackingDetails('');
      setDeliveryCompany('');
    } catch (error) {
      console.error('Error marking product as shipped:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="shipping-details-modal-overlay">
      <div className="shipping-details-modal-content">
        <div className="shipping-details-modal-header">
          <h2>Shipping Details</h2>
          <button 
            className="shipping-details-modal-close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="shipping-details-modal-body">
          {creatorData.deliveryAddress ? (
            <div className="shipping-address-card">
              <div className="shipping-address-header">
                <h3>Delivery Address</h3>
                <div className="shipping-status-badge">
                  {creatorData.progress?.productSent ? 'Product Shipped' : 'Awaiting Shipment'}
                </div>
              </div>
              
              <div className="shipping-address-details">
                <div className="address-field">
                  <label>Contact Person</label>
                  <p>{creatorData.deliveryAddress.contactPerson || creatorData.name}</p>
                </div>
                
                <div className="address-field">
                  <label>Address</label>
                  <div className="address-lines">
                    {creatorData.deliveryAddress.addressLine1 && (
                      <p>{creatorData.deliveryAddress.addressLine1}</p>
                    )}
                    {creatorData.deliveryAddress.addressLine2 && (
                      <p>{creatorData.deliveryAddress.addressLine2}</p>
                    )}
                    {!creatorData.deliveryAddress.addressLine1 && creatorData.deliveryAddress.address && (
                      <p>{creatorData.deliveryAddress.address}</p>
                    )}
                    <p>
                      {creatorData.deliveryAddress.city}
                      {creatorData.deliveryAddress.state && `, ${creatorData.deliveryAddress.state}`}
                      {creatorData.deliveryAddress.postalCode && ` ${creatorData.deliveryAddress.postalCode}`}
                      {!creatorData.deliveryAddress.postalCode && creatorData.deliveryAddress.zipCode && ` ${creatorData.deliveryAddress.zipCode}`}
                    </p>
                    {creatorData.deliveryAddress.country && (
                      <p>{creatorData.deliveryAddress.country}</p>
                    )}
                  </div>
                </div>

                {creatorData.deliveryAddress.phone && (
                  <div className="address-field">
                    <label>Phone</label>
                    <p>{creatorData.deliveryAddress.phone}</p>
                  </div>
                )}

                {creatorData.deliveryAddress.specialInstructions && (
                  <div className="address-field">
                    <label>Special Instructions</label>
                    <p>{creatorData.deliveryAddress.specialInstructions}</p>
                  </div>
                )}

                {creatorData.deliveryAddress.expectedDeliveryDate && (
                  <div className="address-field">
                    <label>Expected Delivery Date</label>
                    <p>{new Date(creatorData.deliveryAddress.expectedDeliveryDate).toLocaleDateString()}</p>
                  </div>
                )}

              </div>
            </div>
          ) : (
            <div className="no-address-message">
              <p>No delivery address provided yet</p>
            </div>
          )}
        </div>

        <div className="shipping-details-modal-footer">
          {creatorData.deliveryAddress && !creatorData.progress?.productSent && (
            <button 
              className="product-shipped-btn"
              onClick={() => setShowConfirmation(true)}
              disabled={isLoading}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="3.27,6.96 12,12.01 20.73,6.96" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="22.08" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {isLoading ? 'Updating...' : 'Mark Product as Shipped'}
            </button>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="shipping-confirmation-overlay">
          <div className="shipping-confirmation-modal">
            <div className="shipping-confirmation-header">
              <h3>Confirm Product Shipment</h3>
            </div>
            <div className="shipping-confirmation-body">
              <p>You are about to mark that you have shipped the product.</p>
              <ul className="shipping-confirmation-list">
                <li>This will notify the creator that they should be expecting your product soon.</li>
                <li>You will no longer be able to access the creator's address once confirmed.</li>
              </ul>
              
              <div className="shipping-confirmation-tracking">
                <div className="shipping-confirmation-tracking-row">
                  <div className="shipping-confirmation-tracking-field">
                    <label htmlFor="delivery-company" className="shipping-confirmation-tracking-label">
                      Delivery Company (Optional)
                    </label>
                    <select
                      id="delivery-company"
                      value={deliveryCompany}
                      onChange={(e) => setDeliveryCompany(e.target.value)}
                      className="shipping-confirmation-tracking-select"
                      disabled={isLoading}
                    >
                      <option value="">Select delivery company...</option>
                      <option value="Royal Mail">Royal Mail</option>
                      <option value="DPD">DPD</option>
                      <option value="DHL">DHL</option>
                      <option value="UPS">UPS</option>
                      <option value="FedEx">FedEx</option>
                      <option value="Hermes">Hermes</option>
                      <option value="Yodel">Yodel</option>
                      <option value="Parcelforce">Parcelforce</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  <div className="shipping-confirmation-tracking-field">
                    <label htmlFor="tracking-details" className="shipping-confirmation-tracking-label">
                      Tracking Number (Optional)
                    </label>
                    <input
                      id="tracking-details"
                      type="text"
                      value={trackingDetails}
                      onChange={(e) => setTrackingDetails(e.target.value)}
                      placeholder="Enter tracking number..."
                      className="shipping-confirmation-tracking-input"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                
                <p className="shipping-confirmation-tracking-help">
                  This information will be shared with the creator so they can track their package.
                </p>
              </div>
              
              <p className="shipping-confirmation-warning">Are you sure you want to proceed?</p>
            </div>
            <div className="shipping-confirmation-footer">
              <button 
                className="shipping-confirmation-cancel-btn"
                onClick={() => setShowConfirmation(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                className="shipping-confirmation-confirm-btn"
                onClick={handleProductShipped}
                disabled={isLoading}
              >
                {isLoading ? 'Confirming...' : 'Confirm Shipment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShippingDetailsModal;
